import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Switch,
  Space,
  Tag,
  Select,
  InputNumber,
  Typography,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  CheckOutlined,
  CloseOutlined,
  MenuOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import routes from "../../../requests/routes";
import axios from "../../../requests/axios";
import RichText from "../RichTextEditor/RichTextEditor";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import TableWithExport from "../../../generic components/TableWithExport/TableWithExport";

const { Text } = Typography;

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const orderKey = (o) =>
  o == null || Number(o) <= 0 ? Number.POSITIVE_INFINITY : Number(o);

const DROPPABLE_ID = "careers-table-droppable";

export default function CareersAdmin() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [q, setQ] = useState("");
  const [pubFilter, setPubFilter] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  // inline order edit
  const [editingId, setEditingId] = useState(null);
  const [orderDraft, setOrderDraft] = useState(1);
  const [savingOrder, setSavingOrder] = useState(false);

  // DnD reorder mode
  const [reorderMode, setReorderMode] = useState(false);
  const [savingReorder, setSavingReorder] = useState(false);
  const [snapshotBeforeDrag, setSnapshotBeforeDrag] = useState(null);

  const fetchList = async () => {
    try {
      setLoading(true);
      const params = { page, limit: pageSize };
      if (q.trim()) params.q = q.trim();
      if (pubFilter !== "all") params.published = pubFilter;

      const { data } = await axios.get(routes.careersAdminList, { params });
      const list = data?.careers || [];
      list.sort(
        (a, b) =>
          orderKey(a.order) - orderKey(b.order) ||
          new Date(b.created_at || b.createdAt || 0) -
            new Date(a.created_at || a.createdAt || 0)
      );
      setRows(list);
      setTotal(data?.total ?? list.length);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load careers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [page, pageSize, pubFilter]);

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      title: record.title || "",
      summary: record.summary || "",
      description: record.description || "",
      qualifications: record.qualifications || [],
      benefits: record.benefits || [],
      location: record.location || "",
      employmentType: record.employmentType || "",
      applicationUrl: record.applicationUrl || "",
      isPublished: !!record.isPublished,
      order: record.order ?? null,
    });
    setModalOpen(true);
  };

  const submitForm = async (values) => {
    try {
      if (editing) {
        await axios.patch(`${routes.editCareer}/${editing._id}`, values);
      } else {
        await axios.post(routes.addCareer, { ...values, place: "end" });
      }
      setModalOpen(false);
      fetchList();
    } catch (e) {
      message.error(e?.response?.data?.message || "Save failed");
    }
  };

  const togglePublish = async (record, checked) => {
    try {
      await axios.patch(`${routes.editCareer}/${record._id}`, {
        isPublished: checked,
      });
      setRows((prev) =>
        prev.map((r) =>
          r._id === record._id ? { ...r, isPublished: checked } : r
        )
      );
    } catch (e) {
      message.error("Failed to update publish state");
    }
  };

  const deleteOne = (record) => {
    Modal.confirm({
      title: `Delete "${record.title}"?`,
      okType: "danger",
      onOk: async () => {
        try {
          await axios.delete(`${routes.careersAdminItem}/${record._id}`);
          fetchList();
        } catch (e) {
          message.error("Delete failed");
        }
      },
    });
  };

  const duplicateOne = async (record) => {
    Modal.confirm({
      title: `Duplicate "${record.title}"?`,
      okText: "Duplicate",
      onOk: async () => {
        try {
          const { data } = await axios.patch(
            `${routes.duplicateCareer}/${record._id}`
          );
          const c = data?.career || data?.item || data?.program;
          if (c) setRows((prev) => [c, ...prev]);
          fetchList();
        } catch (e) {
          message.error("Duplicate failed");
        }
      },
    });
  };

  const columns = useMemo(() => {
    const base = [
      {
        title: "#",
        width: 64,
        align: "center",
        render: (_v, _r, i) => (page - 1) * pageSize + i + 1,
      },
      {
        title: "Title",
        dataIndex: "title",
        sorter: reorderMode
          ? false
          : (a, b) => (a.title || "").localeCompare(b.title || ""),
        render: (v) => (
          <Text strong ellipsis={{ tooltip: v }}>
            {v || "—"}
          </Text>
        ),
      },
      {
        title: "Published",
        width: 130,
        render: (_, r) => (
          <Space>
            <Switch
              checked={!!r.isPublished}
              onChange={(c) => togglePublish(r, c)}
            />
            {r.isPublished ? (
              <Tag color="green">Published</Tag>
            ) : (
              <Tag>Draft</Tag>
            )}
          </Space>
        ),
      },
      {
        title: "Created",
        width: 200,
        sorter: reorderMode
          ? false
          : (a, b) =>
              new Date(a.created_at || a.createdAt || 0) -
              new Date(b.created_at || b.createdAt || 0),
        render: (_, r) => fmtDate(r.created_at || r.createdAt),
      },
      {
        title: "Actions",
        fixed: "right",
        render: (_, record) => (
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
            <Button
              icon={<CopyOutlined />}
              onClick={() => duplicateOne(record)}
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => deleteOne(record)}
            />
          </Space>
        ),
      },
    ];

    return reorderMode
      ? [
          {
            title: "",
            dataIndex: "__drag",
            width: 44,
            render: () => (
              <MenuOutlined style={{ cursor: "grab", color: "#999" }} />
            ),
          },
          ...base,
        ]
      : base;
  }, [page, pageSize, editingId, orderDraft, savingOrder, reorderMode]);

  return (
    <div>
      <h2 className="adminTitle" style={{ marginBottom: 16 }}>
        Careers
      </h2>

      <TableWithExport
        columns={columns}
        data={rows}
        loading={loading}
        tableName="careers"
        filters={false}
        enableReorder
        addRow={setModalOpen}
        onReorder={async (ids, orderedRows) => {
          await axios.patch(routes.reorderCareers, { ids });
          await fetchList();
        }}
      />

      <Modal
        open={modalOpen}
        title={editing ? `Edit: ${editing.title}` : "Add Career"}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText={editing ? "Save" : "Create"}
        destroyOnClose
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={submitForm}
          initialValues={{
            title: "",
            summary: "",
            description: "",
            qualifications: [],
            benefits: [],
            location: "",
            employmentType: "",
            applicationUrl: "",
            isPublished: false,
            order: null,
          }}
        >
          <Form.Item
            label="Job Title"
            name="title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="e.g. Senior Backend Engineer" />
          </Form.Item>

          <Form.Item label="Summary" name="summary">
            <Input.TextArea rows={3} placeholder="Short one-liner/teaser" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: "Description is required" },
              {
                validator: (_, v) => {
                  const plain = String(v || "")
                    .replace(/<[^>]+>/g, "")
                    .trim();
                  return plain.length >= 10
                    ? Promise.resolve()
                    : Promise.reject("Minimum 10 characters");
                },
              },
            ]}
          >
            <RichText
              value={form.getFieldValue("description")}
              onChange={(html) => form.setFieldsValue({ description: html })}
            />
          </Form.Item>

          <Form.Item label="Qualifications" name="qualifications">
            <Select
              mode="tags"
              placeholder="Type and press Enter to add tags"
            />
          </Form.Item>

          <Form.Item label="Benefits" name="benefits">
            <Select
              mode="tags"
              placeholder="Type and press Enter to add tags"
            />
          </Form.Item>

          <Space style={{ gap: 12 }} wrap>
            <Form.Item
              label="Location"
              name="location"
              style={{ flex: 1, minWidth: 240 }}
            >
              <Input placeholder="e.g. Cairo, Egypt" />
            </Form.Item>
            <Form.Item
              label="Employment Type"
              name="employmentType"
              style={{ flex: 1, minWidth: 240 }}
            >
              <Input placeholder="e.g. Full-time" />
            </Form.Item>
          </Space>

          <Form.Item
            label="Application URL"
            name="applicationUrl"
            rules={[{ required: true, message: "Application URL is required" }]}
          >
            <Input placeholder="https://…" />
          </Form.Item>

          <Space align="center" style={{ marginBottom: 8 }} wrap>
            <Form.Item
              label="Published"
              name="isPublished"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Order (optional)"
              name="order"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={1}
                placeholder="Leave empty to auto-place at end"
              />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
